/**
 * Employee entity ↔ wizard flat field names ↔ nested general-info payload.
 *
 * These mirror the employee-edit wizard's own mappers (payloadNormalize in
 * getEmployeeWorker, makePayload in employeePublishWorker) but restricted to
 * the GENERAL information side — every managerial field (employeeId, joining
 * date, department, branch, designation, employee category, reporting manager,
 * shift, calendar, official/login email) is intentionally omitted so a change
 * request can never touch them. Keeping the flat names identical lets us reuse
 * the actual wizard step components (General/Contact/Family/Identification/
 * Academic/Others) unchanged.
 */

// Flatten the nested list rows the entity stores into the flat per-row fields
// the wizard's Nominee/Insurance/WorkExperience components expect (mirrors the
// *PayloadNormalize helpers in the employee getEmployeeWorker).
const flattenNominees = (nominees) =>
    !nominees
        ? nominees
        : nominees.map((nominee) => {
              const { information } = nominee;
              if (!information) return nominee;
              const { firstname, email, address, relation, occupation, contact } = information;
              return {
                  ...nominee,
                  firstname,
                  email,
                  address: address?.address,
                  relation,
                  occupation,
                  contact
              };
          });

const flattenInsurances = (insurances) =>
    !insurances
        ? insurances
        : insurances.map((insurance) => {
              const { information } = insurance;
              if (!information) return insurance;
              const { firstname, relation, occupation } = information;
              return { ...insurance, firstname, relation, occupation };
          });

const flattenWorkExperiences = (experiences) =>
    !experiences
        ? experiences
        : experiences.map((exp) => ({
              ...exp,
              skills: Array.isArray(exp.skills) ? exp.skills.join() : exp.skills,
              contributions: Array.isArray(exp.contributions) ? exp.contributions.join() : exp.contributions,
              achievements: Array.isArray(exp.achievements) ? exp.achievements.join() : exp.achievements
          }));

/** Employee entity → flat form values for the reused wizard steps. */
export const employeeToFlat = (employee = {}) => {
    const {
        initial, firstname, middlename, lastname, nameInBengali,
        gender, religion, bloodGroup, placeOfBirth, dateOfBirth, maritalStatus,
        father, mother, contact, family, identification, academic,
        nominees, references, insurances, bank, workExperiences, relatedEmployees
    } = employee;

    return {
        initial, firstname, lastname, nameInBengali, middlename,
        gender, bloodGroup, dateOfBirth, placeOfBirth, religion, maritalStatus,

        fatherNameInEng: father?.firstname,
        fatherNameInBn: father?.nameInBengali,
        fatherOccupation: father?.occupation,
        motherNameInEng: mother?.firstname,
        motherNameInBn: mother?.nameInBengali,
        motherOccupation: mother?.occupation,

        presentAddress: contact?.presentAddress?.address,
        presentThana: contact?.presentAddress?.thana,
        presentDistrict: contact?.presentAddress?.district,
        presentMobileNo: contact?.presentAddress?.phoneNumber,
        presentCountry: contact?.presentAddress?.country,

        permanentAddress: contact?.permanentAddress?.address,
        thana: contact?.permanentAddress?.thana,
        district: contact?.permanentAddress?.district,
        country: contact?.permanentAddress?.country,
        permanentMobileNo: contact?.permanentAddress?.phoneNumber,
        personalMobileNo: contact?.mobileNumber || '',
        personalEmail: contact?.personalEmail || '',
        // Read-only in the wizard (locked on mode='update') — shown for context
        // only; flatToGeneralInfo never sends it back, so it can't be changed.
        officialEmail: contact?.officialEmail || '',

        emergencyName: contact?.emergencyContact?.firstname,
        emergencyMobileNo: contact?.emergencyContact?.contact,
        emergencyAddress: contact?.emergencyContact?.address?.address,
        emergencyRelationWithEmployee: contact?.emergencyContact?.relation,

        marriageDate: family?.marriageDate,
        spouseName: family?.spouse?.firstname,
        spouseOccupation: family?.spouse?.occupation,
        spouseLevelOfEducation:
            family?.spouse?.education?.institutions?.[0]?.qualification,
        children: family?.children,
        isMarried: family?.isMarried,

        passportNumber: identification?.passport?.number,
        passportIssuePlace: identification?.passport?.issuePlace,
        passportIssueDate: identification?.passport?.issueDate,
        passportExpiryDate: identification?.passport?.expiryDate,
        tin: identification?.tin,
        nationality: identification?.nationality,
        nidNumber: identification?.nidNumber,
        drivingLicenseNumber: identification?.drivingLicenseNumber,
        photograph: identification?.photograph,
        etin: identification?.etin,
        signature: identification?.signature,
        nid: identification?.nid,

        qualifications: academic?.institutions,
        bankName: bank?.name,
        branch: bank?.branch,
        acNo: bank?.accountNumber,
        references,
        nominees: flattenNominees(nominees),
        insurances: flattenInsurances(insurances),
        workExperiences: flattenWorkExperiences(workExperiences),
        relatedEmployees
    };
};

const makeArray = (value) =>
    typeof value === 'string' ? value.split(',') : value;

const makeNomineePayload = (nominees) =>
    !nominees
        ? []
        : nominees.map(({ firstname, email, address, relation, occupation, contact, id, ...rest }) => ({
              ...rest,
              information: {
                  firstname, email, relation, occupation, contact,
                  lastname: firstname ? ' ' : null,
                  address: {
                      address,
                      thana: address ? ' ' : null,
                      district: address ? ' ' : null,
                      city: address ? ' ' : null,
                      country: address ? ' ' : null,
                      phoneNumber: address ? ' ' : null
                  }
              }
          }));

const makeInsurancePayload = (insurances) =>
    !insurances
        ? []
        : insurances.map(({ firstname, occupation, relation, id, ...rest }) => ({
              ...rest,
              information: { firstname, occupation, relation, lastname: firstname ? ' ' : null }
          }));

const makeWorkExperiencePayload = (experiences) =>
    !experiences
        ? []
        : experiences.map((exp) => ({
              ...exp,
              skills: makeArray(exp.skills),
              contributions: makeArray(exp.contributions),
              achievements: makeArray(exp.achievements)
          }));

/**
 * Flat wizard values → nested general-info payload (matches the employee
 * entity shape the backend deep-merges). Managerial fields and the login
 * account are deliberately excluded.
 */
export const flatToGeneralInfo = (flat = {}) => {
    const {
        initial, firstname, lastname, nameInBengali, middlename,
        gender, bloodGroup, dateOfBirth, placeOfBirth, religion, maritalStatus,
        fatherNameInEng, fatherNameInBn, fatherOccupation,
        motherOccupation, motherNameInBn, motherNameInEng,
        presentAddress, presentThana, presentDistrict, presentMobileNo, presentCountry,
        permanentAddress, permanentMobileNo, thana, district, country,
        personalMobileNo, personalEmail,
        emergencyName, emergencyMobileNo, emergencyAddress, emergencyRelationWithEmployee,
        marriageDate, spouseName, spouseLevelOfEducation, spouseOccupation, children, isMarried,
        passportNumber, passportIssuePlace, passportIssueDate, passportExpiryDate,
        tin, nationality, nidNumber, drivingLicenseNumber,
        photograph, signature, nid, etin,
        qualifications, workExperiences,
        bankName, branch, acNo, nominees, references, insurances, relatedEmployees
    } = flat;

    return {
        initial, firstname, middlename, lastname, nameInBengali,
        gender, religion, bloodGroup, placeOfBirth, dateOfBirth, maritalStatus,
        father: { firstname: fatherNameInEng, nameInBengali: fatherNameInBn, occupation: fatherOccupation },
        mother: { firstname: motherNameInEng, nameInBengali: motherNameInBn, occupation: motherOccupation },
        contact: {
            presentAddress: {
                address: presentAddress, thana: presentThana, district: presentDistrict,
                city: presentDistrict, country: presentCountry, phoneNumber: presentMobileNo
            },
            permanentAddress: {
                address: permanentAddress, thana, district,
                city: district, country, phoneNumber: permanentMobileNo
            },
            personalEmail: personalEmail || '',
            mobileNumber: personalMobileNo || '',
            emergencyContact: {
                firstname: emergencyName,
                contact: emergencyMobileNo,
                address: {
                    address: emergencyAddress,
                    thana: emergencyAddress ? ' ' : null,
                    district: emergencyAddress ? ' ' : null,
                    city: emergencyAddress ? ' ' : null,
                    country: emergencyAddress ? ' ' : null,
                    phoneNumber: emergencyAddress ? emergencyMobileNo : null
                },
                relation: emergencyRelationWithEmployee
            }
        },
        family: {
            spouse: {
                firstname: spouseName,
                occupation: spouseOccupation,
                education: {
                    institutions: [
                        {
                            qualification: spouseLevelOfEducation,
                            name: spouseLevelOfEducation ? ' ' : null,
                            department: spouseLevelOfEducation ? ' ' : null,
                            result: spouseLevelOfEducation ? ' ' : null,
                            board: spouseLevelOfEducation ? ' ' : null
                        }
                    ]
                }
            },
            marriageDate,
            children,
            isMarried
        },
        identification: {
            passport: {
                number: passportNumber, issuePlace: passportIssuePlace,
                issueDate: passportIssueDate, expiryDate: passportExpiryDate
            },
            tin, nationality, nidNumber, drivingLicenseNumber,
            photograph, etin, signature, nid
        },
        academic: { institutions: qualifications },
        bank: { name: bankName, branch, accountNumber: acNo },
        nominees: makeNomineePayload(nominees),
        references: references || [],
        insurances: makeInsurancePayload(insurances),
        workExperiences: makeWorkExperiencePayload(workExperiences),
        relatedEmployees: relatedEmployees || []
    };
};

/**
 * Walks the nested payload building FormData: file-objects (from InputFile,
 * shape {type:'file', file}) are appended as multipart parts keyed by their
 * bracketed nested path and removed from the JSON; everything else stays in a
 * `proposed` JSON string. Mirrors the wizard's FormData_append_object but
 * splits files out so the request body is clean JSON + file parts.
 */
// List sections (nested paths) that come from repeating wizard components.
const LIST_NESTED_PATHS = [
    'family.children',
    'relatedEmployees',
    'academic.institutions',
    'workExperiences',
    'nominees',
    'references',
    'insurances'
];

const getPath = (obj, path) =>
    path.split('.').reduce((o, k) => (o == null ? o : o[k]), obj);

const deletePath = (obj, path) => {
    const keys = path.split('.');
    let o = obj;
    for (let i = 0; i < keys.length - 1; i += 1) {
        if (o == null) return;
        o = o[keys[i]];
    }
    if (o) delete o[keys[keys.length - 1]];
};

/**
 * Removes list sections that are identical to the initial values so a change
 * request only contains lists the user actually modified. Both sides are run
 * through flatToGeneralInfo first, so the comparison is like-for-like and the
 * entity→flat→nested reshaping never shows up as a false "change".
 */
export const pruneUnchangedLists = (nested, initialNested) => {
    LIST_NESTED_PATHS.forEach((p) => {
        const a = JSON.stringify(getPath(nested, p) ?? null);
        const b = JSON.stringify(getPath(initialNested, p) ?? null);
        if (a === b) deletePath(nested, p);
    });
    return nested;
};

export const buildChangeRequestFormData = (payload, note, email) => {
    const formData = new FormData();

    const walk = (obj, parentKey) => {
        const clone = Array.isArray(obj) ? [] : {};
        Object.keys(obj || {}).forEach((key) => {
            const value = obj[key];
            const path = parentKey ? `${parentKey}[${key}]` : key;
            if (value === undefined || value === null) return;
            if (typeof value === 'object' && value.type === 'file' && value.file) {
                formData.append(path, value.file); // new upload → multipart part
                // omit from JSON; backend fills the stored path back in
            } else if (typeof value === 'object') {
                clone[key] = walk(value, path);
            } else {
                clone[key] = value;
            }
        });
        return clone;
    };

    const cleaned = walk(payload, '');
    formData.append('proposed', JSON.stringify(cleaned));
    if (note) formData.append('note', note);
    if (email) formData.append('email', email); // target employee (own by default)
    return formData;
};
